// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a dataset is, in the terms AYNI XR sells and browses by
 * (docs/AYNI_TOOLS_DATA_PLAN.md, docs/AYNI_XR_PRODUCTS.md section 2):
 *
 *   - `dataKindOf`: real time, forecast, projection, reanalysis, historical
 *     (the streams, from their declared type) or the SOS catalogue;
 *   - `themesOf`: atmosphere, ocean & water, climate, fire & air quality,
 *     land & life, space, people. Streams carry sphere categories
 *     (`atmosphere`, `hydrosphere`, …) and SOS rows carry their own tags
 *     (`Air`, `Water`, …); both map here, and a row can have several;
 *   - `packageOf`: the AYNI XR package a stream belongs to, or null for the
 *     free SOS catalogue. The stream index carries no package, so this is
 *     a rule: fire and air quality by name first, then the hazards (in
 *     "All" only), then climate (projections, reanalyses, records), ocean,
 *     and atmosphere. It is shown to people as a mark ("Ocean"); nothing
 *     is blocked by it until streams are enforced by package (licensing
 *     step 4), and the rule is the one place to correct a stream.
 *
 * Pure: no DOM, no i18n. The words live in the UI's tables.
 */

// From the message table itself, not ../i18n: the Pages Functions compile this file too, without Vite's types.
import type { MessageKey } from '../i18n/messages'
import type { Dataset } from '../types'
import { EDUCATION_ONLY_STREAMS, PLUS_HIGHLIGHTS, XR_HAZARDS, XR_HIGHLIGHTS, XR_LIVE, type XrPackage } from './xrPlans'

export type DataKind = 'real-time' | 'forecast' | 'projection' | 'reanalysis' | 'historical' | 'catalogue'
export const DATA_KINDS: readonly DataKind[] = ['real-time', 'forecast', 'projection', 'reanalysis', 'historical', 'catalogue']

export type Theme = 'atmosphere' | 'ocean' | 'climate' | 'fire-aq' | 'land' | 'space' | 'people'
export const THEMES: readonly Theme[] = ['atmosphere', 'ocean', 'climate', 'fire-aq', 'land', 'space', 'people']

/** A theme's name for people (Browse's theme chips, the Real time list's groups). */
/** AYNI: the words for the kinds, as Browse shows them (the headset's Browse reads them too). */
export const DATA_KIND_LABEL: Readonly<Record<DataKind, MessageKey>> = {
  'real-time': 'browse.kind.realTime',
  forecast: 'browse.kind.forecast',
  projection: 'browse.kind.projection',
  reanalysis: 'browse.kind.reanalysis',
  historical: 'browse.kind.historical',
  catalogue: 'browse.kind.catalogue',
}

export const THEME_LABEL: Readonly<Record<Theme, MessageKey>> = {
  atmosphere: 'browse.theme.atmosphere',
  ocean: 'browse.theme.ocean',
  climate: 'browse.theme.climate',
  'fire-aq': 'browse.theme.fireAq',
  land: 'browse.theme.land',
  space: 'browse.theme.space',
  people: 'browse.theme.people',
}

/** A stream's package: one of the four, the hazards (in "All" only), or none. */
export type StreamPackage = XrPackage | typeof XR_HAZARDS

/**
 * The package rule's name patterns, checked against the 143 live streams
 * (2026-10-03) and the grouping in docs/AYNI_XR_PRODUCTS.md section 2.
 */
/**
 * Hazards and events. Named ones only: "Coral Bleaching Alert" is ocean, not a hazard.
 * Tropical cyclones and tsunami arrival are hazards (owner, 2026-10-13).
 */
const HAZARDS = /\b(earthquakes?|seismic|gdacs|disasters?|gdelt|global events|sun|solar|sdo|cyclones?|tsunamis?)\b/i
/** Fire, smoke and air quality, by name only (the `health` category also holds heat and UV). */
const FIRE_AQ = /\b(fires?|firms|wildfires?|burned|fire danger|smoke|aerosols?|aerosol optical depth|aod|black ?carbon|no2|nitrogen dioxide|so2|sulphur dioxide|sulfur dioxide|ozone|o3|pm ?2\.?5|air quality|carbon monoxide|dust)\b/i
/** Ocean, by name — projections of the sea included (aragonite saturation: owner, 2026-10-13); sea ice is climate. */
const OCEAN = /\b(ocean|oceanic|sea level|sea surface|sst|marine|coral|chlorophyll|currents?|waves?|tides?|vessels?|salinity|acidification|aragonite)\b/i
/** Climate records whatever their sphere: greenhouse gases, snow and ice, land water, vegetation, people. */
const CLIMATE = /\b(methane|ch4|carbon dioxide|co2|snow|ice|vegetation|ndvi|drought|soil water|water stored on land|grace|built-up|where people live|population)\b/i

const THEME_TAGS: Readonly<Record<Theme, readonly string[]>> = {
  atmosphere: ['atmosphere', 'air'],
  ocean: ['hydrosphere', 'water'],
  climate: ['climate', 'cryosphere', 'snow and ice'],
  'fire-aq': [],
  land: ['lithosphere', 'biosphere', 'land'],
  space: ['space'],
  people: ['humans', 'people'],
}

/** The fields the package rule reads: a full `Dataset`, or a stream row's shape (`streamPackageRules.ts`). */
export type PackageSubject = Pick<Dataset, 'id' | 'title' | 'tags' | 'realtimeKind'>

function tagsOf(dataset: PackageSubject): string[] {
  return (dataset.tags ?? []).map((t) => t.toLowerCase())
}

function nameOf(dataset: PackageSubject): string {
  return `${dataset.id} ${dataset.title}`.replace(/_/g, ' ')
}

export function dataKindOf(dataset: Dataset): DataKind {
  return dataset.realtimeKind ?? 'catalogue'
}

export function themesOf(dataset: Dataset): Theme[] {
  const tags = tagsOf(dataset)
  const themes = THEMES.filter((theme) => THEME_TAGS[theme].some((tag) => tags.includes(tag)))
  if (!themes.includes('fire-aq') && FIRE_AQ.test(nameOf(dataset))) themes.push('fire-aq')
  return themes
}

/** The package a stream is sold in; null for the free SOS catalogue (and anything not a stream). */
export function packageOf(dataset: PackageSubject): StreamPackage | null {
  const kind = dataset.realtimeKind
  if (!kind) return null
  const tags = tagsOf(dataset)
  const name = nameOf(dataset)
  if (HAZARDS.test(name)) return XR_HAZARDS
  if (FIRE_AQ.test(name)) return 'xr-fire-aq'
  if (OCEAN.test(name) && !/\bsea ice\b/i.test(name)) return 'xr-ocean'
  if (kind === 'projection' || kind === 'reanalysis' || kind === 'historical' || CLIMATE.test(name)) return 'xr-climate'
  // Live and forecast weather is the atmosphere package, even when also filed under climate.
  if (tags.includes('atmosphere')) return 'xr-atmosphere'
  if (['climate', 'cryosphere', 'biosphere', 'humans', 'hydrosphere'].some((t) => tags.includes(t))) return 'xr-climate'
  return null
}

/**
 * AYNI: the package a stream belongs to when the licence's packages
 * (`currentEntitlements().packages`) do not cover it, else null. The
 * browse panel marks such a row with a lock and a link to the plans
 * (owner, 2026-10-03: "upgrade hint"). Marking only — licensing step 4's
 * observe mode: every stream still plays.
 */
export function outsidePlan(dataset: Dataset, packages: ReadonlySet<string> | 'all', educationStreams = false): StreamPackage | null {
  const pkg = packageOf(dataset)
  // AYNI (owner, 2026-10-18): a stream only Education plays is outside every other plan, whatever its packages.
  if (isEducationOnly(dataset) && !educationStreams) return pkg ?? 'xr-ocean'
  if (!pkg || packages === 'all') return null
  if (packages.has(pkg)) return null
  // AYNI (Plus, owner 2026-10-17): Live Earth is every real-time stream and forecast; the highlights, a list.
  if (packages.has(XR_LIVE) && (dataset.realtimeKind === 'real-time' || dataset.realtimeKind === 'forecast')) return null
  if (packages.has(XR_HIGHLIGHTS) && isPlusHighlight(dataset)) return null
  return pkg
}

const NO_PACKAGES: ReadonlySet<string> = new Set()

/** Whether the free plan opens a row: the SOS catalogue and any stream no package sells (Browse's Free filter). */
export function isFree(dataset: Dataset): boolean {
  return outsidePlan(dataset, NO_PACKAGES) === null
}

/** Whether a stream is for Education plans only (`EDUCATION_ONLY_STREAMS`, by the index's stable id). */
export function isEducationOnly(dataset: Pick<Dataset, 'id'>): boolean {
  return dataset.id.startsWith('R2_DASH_') && EDUCATION_ONLY_STREAMS.has(dataset.id.slice('R2_DASH_'.length))
}

/** Whether a stream is one of Plus's climate highlights (by the index's stable id). */
export function isPlusHighlight(dataset: Pick<Dataset, 'id'>): boolean {
  return dataset.id.startsWith('R2_DASH_') && PLUS_HIGHLIGHTS.has(dataset.id.slice('R2_DASH_'.length))
}

/** A kind written at the start of a title, singular or plural ("Projections: …"). */
export const KIND_PREFIX = /^(?:Real Time|Forecasts?|Projections?|Reanalys[ie]s|Historical):\s*/i

/**
 * The title without the kind the badge now shows ("Real Time: Wind" →
 * "Wind"). The stored title keeps the prefix, because search, the AI
 * guide and saved Khipus read it.
 */
export function displayTitle(dataset: Dataset): string {
  if (!dataset.realtimeKind) return dataset.title
  return dataset.title.replace(KIND_PREFIX, '') || dataset.title
}
